import { useState, useEffect } from "react";
import { FiX } from "react-icons/fi";
import cronstrue from "cronstrue";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const UpdateCronModal = ({ isOpen, onClose, taskName, currentCron, onUpdate, isLoading }) => {
  const [selectedValue, setSelectedValue] = useState("");
  const [selectedDay, setSelectedDay] = useState(11);
  const [selectedHour, setSelectedHour] = useState(2);
  const [selectedInterval, setSelectedInterval] = useState("hourly");
  const [selectedMinuteInterval, setSelectedMinuteInterval] = useState("15");
  const [backupHour, setBackupHour] = useState(11);

  useEffect(() => {
    if (currentCron && isOpen) {
      parseCurrentCron(currentCron);
    }
  }, [currentCron, isOpen]);

  const parseCurrentCron = (cron) => {
    const parts = cron.split(" ");
    const minute = parts[0];
    const hour = parts[1];
    const dayOfMonth = parts[3];

    if (taskName === "performance") {
      if (hour === "0" && parts[2] === "*") {
        setSelectedValue("1");
      } else if (parts[2] === "*/2") {
        setSelectedValue("2");
      } else if (parts[2] === "*/4") {
        setSelectedValue("4");
      } else if (parts[2] === "*/6") {
        setSelectedValue("6");
      } else if (parts[2] === "*/12") {
        setSelectedValue("12");
      } else if (hour === "0" && parts[2] === "0") {
        setSelectedValue("24");
      }
    } 
    else if (taskName === "infra" || taskName === "alert") {
      if (minute === "*/15") {
        setSelectedMinuteInterval("15");
        setSelectedInterval("minute");
      } else if (minute === "*/30") {
        setSelectedMinuteInterval("30");
        setSelectedInterval("minute");
      } else if (hour === "0" && parts[2] === "*") {
        setSelectedValue("1");
        setSelectedInterval("hourly");
      } else if (parts[2] === "*/2") {
        setSelectedValue("2");
        setSelectedInterval("hourly");
      } else if (parts[2] === "*/4") {
        setSelectedValue("4");
        setSelectedInterval("hourly");
      } else if (parts[2] === "*/6") {
        setSelectedValue("6");
        setSelectedInterval("hourly");
      } else if (parts[2] === "*/12") {
        setSelectedValue("12");
        setSelectedInterval("hourly");
      } else if (hour === "0" && parts[2] === "0") {
        setSelectedValue("24");
        setSelectedInterval("hourly");
      }
    }
    else if (taskName === "invoice" || taskName === "monthlyCost") {
      if (dayOfMonth !== "*") {
        setSelectedDay(parseInt(dayOfMonth));
      }
      if (hour !== "*") {
        setSelectedHour(parseInt(hour));
      }
    }
    else if (taskName === "backup") {
      if (hour !== "*") {
        setBackupHour(parseInt(hour));
      }
    }
  };

  const buildCronExpression = () => {
    if (taskName === "performance") {
      if (selectedValue === "24") {
        return "0 0 0 * * *";
      }
      return `0 0 */${selectedValue} * * *`;
    }
    
    if (taskName === "infra" || taskName === "alert") {
      if (selectedInterval === "minute") {
        return `0 */${selectedMinuteInterval} * * * *`;
      } else {
        if (selectedValue === "24") {
          return "0 0 0 * * *";
        }
        return `0 0 */${selectedValue} * * *`;
      }
    }
    
    if (taskName === "invoice" || taskName === "monthlyCost") {
      return `0 0 ${selectedHour} ${selectedDay} * *`;
    }

    if (taskName === "backup") {
      return `0 0 ${backupHour} * * *`;
    }
    
    return currentCron;
  };

  const getHumanReadablePreview = () => {
    const cron = buildCronExpression();
    try {
      return cronstrue.toString(cron);
    } catch {
      return "Invalid cron expression";
    }
  };

  const handleUpdate = () => {
    const newCron = buildCronExpression();
    onUpdate(newCron);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative bg-primary-dark rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-md mx-auto p-5 sm:p-6 animate-fade-in-up z-10">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 text-gray-400 hover:text-white transition"
        >
          <FiX className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>

        <h2 className="text-lg sm:text-xl font-bold text-white mb-2">
          Update {taskName.charAt(0).toUpperCase() + taskName.slice(1)} Schedule
        </h2>
        
        <p className="text-gray-400 text-xs sm:text-sm mb-4 sm:mb-6 break-words">
          Current: <code className="bg-gray-800 px-1.5 sm:px-2 py-0.5 rounded text-primary-cyan text-xs">{currentCron}</code>
        </p>

        {taskName === "performance" && (
          <div className="space-y-3 sm:space-y-4">
            <label className="block text-gray-300 text-xs sm:text-sm font-medium mb-2">Run every:</label>
            <div className="space-y-1.5 sm:space-y-2">
              {["1", "2", "4", "6", "12", "24"].map((hours) => (
                <label key={hours} className="flex items-center gap-2 sm:gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="interval"
                    value={hours}
                    checked={selectedValue === hours}
                    onChange={(e) => setSelectedValue(e.target.value)}
                    className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary-cyan focus:ring-primary-cyan"
                  />
                  <span className="text-gray-300 text-sm sm:text-base">
                    Every {hours} {hours === "1" ? "hour" : "hours"}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        {(taskName === "infra" || taskName === "alert") && (
          <div className="space-y-3 sm:space-y-4">
            <div className="flex gap-3 sm:gap-4 mb-3 sm:mb-4">
              <button
                onClick={() => setSelectedInterval("minute")}
                className={`flex-1 py-1.5 sm:py-2 rounded-lg text-sm sm:text-base transition ${selectedInterval === "minute" ? "bg-primary-cyan text-white" : "bg-gray-700 text-gray-300"}`}
              >
                Minutes
              </button>
              <button
                onClick={() => setSelectedInterval("hourly")}
                className={`flex-1 py-1.5 sm:py-2 rounded-lg text-sm sm:text-base transition ${selectedInterval === "hourly" ? "bg-primary-cyan text-white" : "bg-gray-700 text-gray-300"}`}
              >
                Hours
              </button>
            </div>

            {selectedInterval === "minute" ? (
              <div>
                <label className="block text-gray-300 text-xs sm:text-sm font-medium mb-2">Every:</label>
                <div className="space-y-1.5 sm:space-y-2">
                  {["15", "30"].map((minutes) => (
                    <label key={minutes} className="flex items-center gap-2 sm:gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="minuteInterval"
                        value={minutes}
                        checked={selectedMinuteInterval === minutes}
                        onChange={(e) => setSelectedMinuteInterval(e.target.value)}
                        className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary-cyan focus:ring-primary-cyan"
                      />
                      <span className="text-gray-300 text-sm sm:text-base">Every {minutes} minutes</span>
                    </label>
                  ))}
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-gray-300 text-xs sm:text-sm font-medium mb-2">Run every:</label>
                <div className="space-y-1.5 sm:space-y-2">
                  {["1", "2", "4", "6", "12", "24"].map((hours) => (
                    <label key={hours} className="flex items-center gap-2 sm:gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="interval"
                        value={hours}
                        checked={selectedValue === hours}
                        onChange={(e) => setSelectedValue(e.target.value)}
                        className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary-cyan focus:ring-primary-cyan"
                      />
                      <span className="text-gray-300 text-sm sm:text-base">
                        Every {hours} {hours === "1" ? "hour" : "hours"}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {(taskName === "invoice" || taskName === "monthlyCost") && (
          <div className="space-y-3 sm:space-y-4">
            <div>
              <label className="block text-gray-300 text-xs sm:text-sm font-medium mb-2">Day of month:</label>
              <DatePicker
                selected={new Date(2024, 0, selectedDay)}
                onChange={(date) => setSelectedDay(date.getDate())}
                dateFormat="d"
                showMonthDropdown={false}
                showYearDropdown={false}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 sm:px-4 py-1.5 sm:py-2 text-white text-sm sm:text-base focus:border-primary-cyan focus:outline-none cursor-pointer"
                calendarClassName="bg-primary-dark border border-gray-700 rounded-lg shadow-xl"
                dayClassName={() => "text-white hover:bg-primary-cyan/50"}
                renderCustomHeader={({ monthDate, decreaseMonth, increaseMonth }) => (
                  <div className="flex justify-between items-center px-2 py-2 bg-primary-dark">
                    <button onClick={decreaseMonth} className="text-gray-400 hover:text-white transition text-sm sm:text-base">
                      &lt;
                    </button>
                    <span className="text-white font-medium text-sm sm:text-base">
                      {monthDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
                    </span>
                    <button onClick={increaseMonth} className="text-gray-400 hover:text-white transition text-sm sm:text-base">
                      &gt;
                    </button>
                  </div>
                )}
              />
              <p className="text-gray-500 text-[10px] sm:text-xs mt-1">
                Selected day: <span className="text-primary-cyan">{selectedDay}</span> of each month
              </p>
            </div>

            <div>
              <label className="block text-gray-300 text-xs sm:text-sm font-medium mb-2">Hour (24h):</label>
              <DatePicker
                selected={new Date(2024, 0, 1, selectedHour, 0)}
                onChange={(date) => setSelectedHour(date.getHours())}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={60}
                timeCaption="Hour"
                dateFormat="HH:mm"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 sm:px-4 py-1.5 sm:py-2 text-white text-sm sm:text-base focus:border-primary-cyan focus:outline-none cursor-pointer"
              />
              <p className="text-gray-500 text-[10px] sm:text-xs mt-1">
                Selected hour: <span className="text-primary-cyan">{selectedHour}:00</span>
              </p>
            </div>
          </div>
        )}

        {taskName === "backup" && (
          <div className="space-y-3 sm:space-y-4">
            <div>
              <label className="block text-gray-300 text-xs sm:text-sm font-medium mb-2">Hour (24h):</label>
              <DatePicker
                selected={new Date(2024, 0, 1, backupHour, 0)}
                onChange={(date) => setBackupHour(date.getHours())}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={60}
                timeCaption="Hour"
                dateFormat="HH:mm"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 sm:px-4 py-1.5 sm:py-2 text-white text-sm sm:text-base focus:border-primary-cyan focus:outline-none cursor-pointer"
              />
              <p className="text-gray-500 text-[10px] sm:text-xs mt-1">
                Runs daily at <span className="text-primary-cyan">{backupHour}:00</span>
              </p>
            </div>
          </div>
        )}

        <div className="mt-5 sm:mt-6 p-2 sm:p-3 bg-gray-800/50 rounded-lg">
          <p className="text-gray-400 text-[10px] sm:text-xs mb-1">Preview:</p>
          <p className="text-primary-cyan text-xs sm:text-sm break-words">{getHumanReadablePreview()}</p>
        </div>

        <div className="flex gap-2 sm:gap-3 mt-4 sm:mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-3 sm:px-4 py-1.5 sm:py-2 border border-gray-600 rounded-lg text-gray-300 text-sm sm:text-base hover:bg-gray-700 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleUpdate}
            disabled={isLoading}
            className="flex-1 px-3 sm:px-4 py-1.5 sm:py-2 bg-primary-cyan text-white rounded-lg text-sm sm:text-base hover:bg-red-700 transition disabled:opacity-50"
          >
            {isLoading ? "Updating..." : "Update"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpdateCronModal;